<script setup lang="ts">
import type { HTMLAttributes, Ref } from 'vue'

import { defaultDocument, useEventListener, useMediaQuery, useVModel } from '@vueuse/core'
import { cn } from '~ds/lib/utils'
import { TooltipProvider } from 'reka-ui'
import { computed, onMounted, ref } from 'vue'

import { provideSidebarContext, SIDEBAR_COOKIE_MAX_AGE, SIDEBAR_COOKIE_NAME, SIDEBAR_KEYBOARD_SHORTCUT, SIDEBAR_WIDTH, SIDEBAR_WIDTH_ICON } from './utils'

const props = withDefaults(defineProps<{
  defaultOpen?: boolean;
  open?: boolean;
  class?: HTMLAttributes['class'];
}>(), {
  defaultOpen: !defaultDocument?.cookie.includes(`${SIDEBAR_COOKIE_NAME}=false`),
  open: undefined,
})

const emits = defineEmits<{
  'update:open': [open: boolean];
}>()

// SSR always renders the desktop branch (no window), so isMobile must stay
// false through hydration and only track the viewport after mount — otherwise
// a <=768px client hydrates the mobile Sheet against the desktop-rail markup.
// 767px (shadcn/ui's breakpoint - 1, as credenza), not shadcn-vue's 768px, so the mobile sheet
// hands over to the `md:` desktop rail at the same width.
const isMobileQuery = useMediaQuery('(max-width: 767px)')
const isMounted = ref(false)
onMounted(() => {
  isMounted.value = true
})
const isMobile = computed(() => isMounted.value && isMobileQuery.value)
const openMobile = ref(false)

const open = useVModel(props, 'open', emits, {
  defaultValue: props.defaultOpen ?? false,
  passive: (props.open === undefined) as false,
}) as Ref<boolean>

const setOpen = (value: boolean) => {
  open.value = value // emits('update:open', value)

  // This sets the cookie to keep the sidebar state.
  document.cookie = `${SIDEBAR_COOKIE_NAME}=${open.value}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
}

const setOpenMobile = (value: boolean) => {
  openMobile.value = value
}

// Helper to toggle the sidebar.
const toggleSidebar = () => (isMobile.value ? setOpenMobile(!openMobile.value) : setOpen(!open.value))

useEventListener('keydown', (event: KeyboardEvent) => {
  if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
    event.preventDefault()
    toggleSidebar()
  }
})

// We add a state so that we can do data-state="expanded" or "collapsed".
// This makes it easier to style the sidebar with Tailwind classes.
const state = computed(() => open.value ? 'expanded' : 'collapsed')

provideSidebarContext({
  state,
  open,
  setOpen,
  isMobile,
  openMobile,
  setOpenMobile,
  toggleSidebar,
})
</script>

<template>
  <TooltipProvider :delay-duration="0">
    <div
      data-slot="sidebar-wrapper"
      :style="{
        '--sidebar-width': SIDEBAR_WIDTH,
        '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
      }"
      :class="cn('group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full', props.class)"
      v-bind="$attrs"
    >
      <slot
        :state="state"
        :open="open"
        :set-open="setOpen"
        :is-mobile="isMobile"
        :open-mobile="openMobile"
        :set-open-mobile="setOpenMobile"
        :toggle-sidebar="toggleSidebar"
      />
    </div>
  </TooltipProvider>
</template>
